// src/pages/students/StudentDetails.jsx
import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useStudents } from '../../hooks/useStudents';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import TabsNav from './details/TabsNav';
import EnrollmentsTab from './details/EnrollmentsTab';
import PaymentsTab from './details/PaymentsTab';
import DocumentsTab from './details/DocumentsTab';
import ExamsTab from './details/ExamsTab';
import { ArrowLeftIcon, PencilIcon } from '@heroicons/react/24/outline';

const TABS = {
  ENROLLMENTS: 'enrollments',
  PAYMENTS: 'payments',
  DOCUMENTS: 'documents',
  EXAMS: 'exams',
};

// NEW: badge class helper for membership status
const membershipPill = (status) => {
  const s = (status || '').toLowerCase();
  if (s.includes('completed')) return 'bg-emerald-100 text-emerald-800';
  if (s.includes('form f')) return 'bg-indigo-100 text-indigo-800';
  if (s.includes('annual')) return 'bg-amber-100 text-amber-800';
  if (s.includes('awaiting')) return 'bg-sky-100 text-sky-800';
  if (s.includes('created')) return 'bg-blue-100 text-blue-800';
  // default / pending
  return 'bg-gray-100 text-gray-800';
};

const StudentDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const fromHint = useMemo(() => {
    const stateFrom = location?.state?.from || null;
    const queryFrom = new URLSearchParams(location.search).get('from') || null;
    return stateFrom || queryFrom;
  }, [location]);

  const { students } = useStudents();

  const [student, setStudent] = useState(null);
  const [notFound, setNotFound] = useState(false);

  const urlTab = new URLSearchParams(location.search).get('tab');
  const [activeTab, setActiveTab] = useState(
    urlTab && Object.values(TABS).includes(urlTab) ? urlTab : TABS.ENROLLMENTS
  );

  useEffect(() => {
    const found = students.find((s) => s.id === id);
    if (found) setStudent(found);
    else if (students.length > 0) setNotFound(true);
  }, [id, students]);

  const handleBack = () => {
    const target = fromHint === 'dashboard' ? '/' : '/students';
    navigate(target, { replace: true });
  };

  const backLabel = fromHint === 'dashboard' ? 'Back to Dashboard' : 'Back to Students';

  const handleEdit = () => navigate(`/students/${id}/edit`);

  const onTabClick = (tabKey) => {
    setActiveTab(tabKey);
    const params = new URLSearchParams(location.search);
    params.set('tab', tabKey);
    navigate({ search: params.toString() }, { replace: true });

    if (tabKey === TABS.ENROLLMENTS) {
      queryClient.invalidateQueries({ queryKey: ['paid_enrollment_ids', id] });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Invalid Date';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Invalid Date';
      return date.toLocaleDateString();
    } catch {
      return 'Invalid Date';
    }
  };

  if (notFound) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-4">
          <Button variant="outline" onClick={handleBack} className="flex items-center">
            <ArrowLeftIcon className="h-5 w-5 mr-1" />
            {backLabel}
          </Button>
          <h1 className="text-2xl font-bold text-gray-900">Student Not Found</h1>
        </div>
        <Card>
          <div className="py-8 text-center">
            <p className="text-gray-500">The student you are looking for does not exist.</p>
          </div>
        </Card>
      </div>
    );
  }

  if (!student) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-4">
{/*          <Button variant="outline" onClick={handleBack} className="flex items-center">
            <ArrowLeftIcon className="h-5 w-5 mr-1" />
            {backLabel}
          </Button>
*/}
          <h1 className="text-2xl font-bold text-gray-900">Student Details</h1>
        </div>
        <Card>
          <div className="py-8 text-center">
            <p className="text-gray-500">Loading student data...</p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-4">
{/*
          <Button variant="outline" onClick={handleBack} className="flex items-center">
            <ArrowLeftIcon className="h-5 w-5 mr-1" />
            Back
          </Button>
*/}
          <h1 className="text-2xl font-bold text-gray-900">Student Details</h1>
        </div>
      </div>

      {/* Info Card */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <div />
          <Button onClick={handleEdit} className="flex items-center">
            <PencilIcon className="h-4 w-4 mr-1" />
            Edit Student
          </Button>
        </div>

        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 flex-1">
            <div>
              <h3 className="text-sm font-medium text-gray-500">Full Name</h3>
              <p className="mt-1 text-sm text-gray-900">{student.full_name}</p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500">Status</h3>
              <p className="mt-1 text-sm text-gray-900">{student.status}</p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500">NRIC/Passport</h3>
              <p className="mt-1 text-sm text-gray-900">{student.nric_passport}</p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500">Date of Birth</h3>
              <p className="mt-1 text-sm text-gray-900">{formatDate(student.date_of_birth)}</p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500">Email</h3>
              <p className="mt-1 text-sm text-gray-900">{student.email}</p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500">Contact No.</h3>
              <p className="mt-1 text-sm text-gray-900">{student.contact_no || 'Not provided'}</p>
            </div>
            <div className="md:col-span-2">
              <h3 className="text-sm font-medium text-gray-500">Address</h3>
              <p className="mt-1 text-sm text-gray-900">{student.address || 'Not provided'}</p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500">Enrollment Year</h3>
              <p className="mt-1 text-sm text-gray-900">{student.enrollment_year}</p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500">Enrollment Type</h3>
              <p className="mt-1 text-sm text-gray-900">{student.payment_type}</p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500">University Degree</h3>
              <p className="mt-1 text-sm text-gray-900">{student.university_degree || 'Not provided'}</p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500">MQA Recognition</h3>
              <div className="mt-1">
                <span
                  className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                    student.mqa_recognition ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                  }`}
                >
                  {student.mqa_recognition ? 'Yes' : 'No'}
                </span>
              </div>
            </div>
            {/* NEW: RISM Membership Status */}
            <div>
              <h3 className="text-sm font-medium text-gray-500">RISM Membership Status</h3>
              <div className="mt-1">
                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${membershipPill(student.membership_status)}`}>
                  {student.membership_status || 'Pending'}
                </span>
              </div>
            </div>
            {/* END NEW */}
            <div>
              <h3 className="text-sm font-medium text-gray-500">Moodle Access</h3>
              <div className="mt-1">
                <span
                  className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                    student.moodle_access ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                  }`}
                >
                  {student.moodle_access ? 'Yes' : 'No'}
                </span>
              </div>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500">Membership & Exam Remarks</h3>
              <p className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">{student.remarks || '—'}</p>
            </div>

            <div>
              <h3 className="text-sm font-medium text-gray-500">Misc. Remarks</h3>
              <p className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">{student.misc_remarks || '—'}</p>
            </div>
          </div>
        </div>
      </Card>

      {/* Tabs */}
      <TabsNav
        activeTab={activeTab}
        onTabClick={onTabClick}
        tabs={[
          { key: TABS.ENROLLMENTS, label: 'Enrollments' },
          { key: TABS.PAYMENTS, label: 'Payments' },          
          { key: TABS.EXAMS, label: 'Exams' },
          { key: TABS.DOCUMENTS, label: 'Documents' },
        ]}
      />

      {/* Tab bodies */}
      {activeTab === TABS.ENROLLMENTS && <EnrollmentsTab studentId={id} />}
      {activeTab === TABS.PAYMENTS && <PaymentsTab studentId={id} />}
      {activeTab === TABS.DOCUMENTS && <DocumentsTab studentId={id} />}
      {activeTab === TABS.EXAMS && <ExamsTab studentId={id} />}
    </div>
  );
};

export default StudentDetails;
